import React from 'react';
import { Brain, Sparkles } from 'lucide-react';
import type { ReasoningItem } from '../../../types/kyb.types';

interface EnhancedAIAnalysisProps {
  llmInsights?: any;
  reasoning: ReasoningItem[];
}

export const EnhancedAIAnalysis: React.FC<EnhancedAIAnalysisProps> = ({ 
  llmInsights, 
  reasoning 
}) => {
  if (!llmInsights && (!reasoning || reasoning.length === 0)) return null;

  // Helper to shorten URLs for display
  const getShortDisplayName = (url: string): string => {
    try {
      const urlObj = new URL(url);
      let domain = urlObj.hostname.replace(/^www\./, '');
      if (domain.length > 30) {
        return domain.substring(0, 27) + '...';
      }
      return domain;
    } catch {
      return url.length > 30 ? url.substring(0, 27) + '...' : url;
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 rounded-2xl p-8 shadow-2xl">
      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
          <Brain className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-2xl font-bold text-white">Enhanced AI Analysis & Reasoning</h2>
            <Sparkles className="w-6 h-6 text-yellow-300 animate-pulse" />
          </div>
          <p className="text-blue-100 font-medium">AI-powered synthesis and strategic insights</p>
        </div>
        {llmInsights?.confidence && (
          <div className="text-right">
            <div className="text-white/80 text-xs uppercase">Confidence</div>
            <div className="text-3xl font-bold text-white">
              {(llmInsights.confidence * 100).toFixed(0)}%
            </div>
          </div>
        )}
      </div>

      {reasoning && reasoning.length > 0 && (
        <div className="space-y-3">
          {reasoning.map((item, idx) => {
            const isImportant = item.statement.toLowerCase().includes('critical') || 
                              item.statement.toLowerCase().includes('acquired');
            
            return (
              <div 
                key={idx} 
                className={`rounded-lg p-4 ${
                  isImportant ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-white/95'
                }`}
              >
                <p className={`leading-relaxed ${
                  isImportant ? 'text-yellow-900 font-semibold' : 'text-slate-800'
                }`}>
                  {item.statement}
                </p>
                {item.sources && item.sources.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {item.sources.map((source, sIdx) => {
                      const isUrl = source.startsWith('http://') || source.startsWith('https://');
                      
                      return isUrl ? (
                        <a
                          key={sIdx}
                          href={source}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-xs px-2 py-1 rounded hover:opacity-80 transition-opacity inline-flex items-center gap-1 ${
                            isImportant 
                              ? 'bg-yellow-200 text-yellow-800 hover:bg-yellow-300' 
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          title={source}
                        >
                          {getShortDisplayName(source)}
                          <span className="text-[10px]">↗</span>
                        </a>
                      ) : (
                        <span
                          key={sIdx}
                          className={`text-xs px-2 py-1 rounded ${
                            isImportant ? 'bg-yellow-200 text-yellow-800' : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {source}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};